/**
 * Hooks TanStack Query del API /platform/* (Doc 25 §F4).
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import {
  createPlatformTenant,
  getPlatformGlobal,
  getPlatformMargin,
  getPlatformTenantDefaults,
  getPlatformTenantDetail,
  getPlatformTenantSummary,
  listPlatformTenants,
  resendPlatformTenantAdminInvite,
  runBalanceCheck,
  runBillingVerification,
  runPlatformTenantAction,
  type BalanceCheckResult,
  type BillingVerificationResult,
  type PlatformFilters,
  type PlatformMargin,
  type PlatformTenantCreate,
  type PlatformTenantDefaults,
  type PlatformTenantDetail,
  type PlatformTenantListRow,
  type PlatformUsageGlobal,
  type TenantLifecycleAction,
} from '../api/platform'
import { retryUnlessPlatformDenied } from '../api/queryRetry'
import { useAccessToken } from '../auth/useAccessToken'
import { pushToast } from '../stores/toastStore'

const STALE = 30_000
const TENANTS_KEY = ['platform-tenants'] as const
// Raíces de las queries de /platform/usage (resumen, margen y por cliente).
const GLOBAL_KEY = 'platform-global'
const MARGIN_KEY = 'platform-margin'
const TENANT_SUMMARY_KEY = 'platform-tenant-summary'

export function usePlatformTenants(
  options: { enabled?: boolean } = {},
): UseQueryResult<PlatformTenantListRow[], Error> {
  const { accessToken } = useAccessToken()
  const enabled = (options.enabled ?? true) && !!accessToken
  return useQuery({
    queryKey: TENANTS_KEY,
    queryFn: () => {
      if (!accessToken) throw new Error('not_ready')
      return listPlatformTenants(accessToken)
    },
    enabled,
    staleTime: STALE,
  })
}

export function usePlatformGlobal(
  filters: PlatformFilters = {},
  options: { enabled?: boolean } = {},
): UseQueryResult<PlatformUsageGlobal, Error> {
  const { accessToken } = useAccessToken()
  const enabled = (options.enabled ?? true) && !!accessToken
  return useQuery({
    queryKey: [GLOBAL_KEY, filters],
    queryFn: () => {
      if (!accessToken) throw new Error('not_ready')
      return getPlatformGlobal(accessToken, filters)
    },
    enabled,
    staleTime: STALE,
  })
}

export function usePlatformMargin(
  filters: PlatformFilters = {},
  options: { enabled?: boolean } = {},
): UseQueryResult<PlatformMargin, Error> {
  const { accessToken } = useAccessToken()
  const enabled = (options.enabled ?? true) && !!accessToken
  return useQuery({
    queryKey: [MARGIN_KEY, filters],
    queryFn: () => {
      if (!accessToken) throw new Error('not_ready')
      return getPlatformMargin(accessToken, filters)
    },
    enabled,
    staleTime: STALE,
  })
}

export function usePlatformTenantSummary(
  tenantId: string | null,
  filters: PlatformFilters = {},
  options: { enabled?: boolean } = {},
): UseQueryResult<PlatformUsageGlobal, Error> {
  const { accessToken } = useAccessToken()
  const enabled =
    (options.enabled ?? true) && !!accessToken && !!tenantId
  return useQuery({
    queryKey: [TENANT_SUMMARY_KEY, tenantId, filters],
    queryFn: () => {
      if (!accessToken || !tenantId) throw new Error('not_ready')
      return getPlatformTenantSummary(accessToken, tenantId, filters)
    },
    enabled,
    staleTime: STALE,
  })
}

/**
 * Mutations de maintenance (Doc 25 §F5).
 *
 * En F.pre el cron real corre como ACA Job (`hivanexus-${env}-balance-check`
 * y `-billing-verification`); estos hooks quedan para el disparo manual
 * del platform admin desde `/platform`.
 */
export function useRunBalanceCheck(): UseMutationResult<
  BalanceCheckResult,
  Error,
  void
> {
  const { accessToken } = useAccessToken()
  return useMutation({
    mutationFn: () => {
      if (!accessToken) throw new Error('not_ready')
      return runBalanceCheck(accessToken)
    },
  })
}

export function useRunBillingVerification(): UseMutationResult<
  BillingVerificationResult,
  Error,
  void
> {
  const { accessToken } = useAccessToken()
  return useMutation({
    mutationFn: () => {
      if (!accessToken) throw new Error('not_ready')
      return runBillingVerification(accessToken)
    },
  })
}

// ─── Consola de clientes (OT-0073) ────────────────────────────────────────────


function tenantDetailKey(tenantId: string) {
  return ['platform-tenant', tenantId] as const
}

export function usePlatformTenantDefaults(): UseQueryResult<PlatformTenantDefaults, Error> {
  const { accessToken } = useAccessToken()
  return useQuery({
    queryKey: ['platform-tenant-defaults'],
    queryFn: () => {
      if (!accessToken) throw new Error('not_ready')
      return getPlatformTenantDefaults(accessToken)
    },
    enabled: !!accessToken,
    staleTime: Infinity,
    // Es también la sonda de acceso de `PlatformAccessGate`.
    retry: (failureCount, error: Error) => retryUnlessPlatformDenied(failureCount, error),
  })
}

export function usePlatformTenantDetail(
  tenantId: string,
): UseQueryResult<PlatformTenantDetail, Error> {
  const { accessToken } = useAccessToken()
  return useQuery({
    queryKey: tenantDetailKey(tenantId),
    queryFn: () => {
      if (!accessToken) throw new Error('not_ready')
      return getPlatformTenantDetail(accessToken, tenantId)
    },
    enabled: !!accessToken,
    staleTime: STALE,
  })
}

/**
 * Tras una escritura, el detalle que responde el backend reemplaza al de la
 * caché y la lista se vuelve a pedir (cambia estado, saldo o admin).
 *
 * El toast de éxito de cada escritura va en el `onSuccess` del hook y no en
 * el de `mutate`: si el diálogo se cierra antes de que responda el backend,
 * el componente se desmonta y el callback de `mutate` ya no corre.
 */
function useStoreTenantDetail(): (detail: PlatformTenantDetail) => void {
  const queryClient = useQueryClient()
  return (detail) => {
    queryClient.setQueryData(tenantDetailKey(detail.tenant_id), detail)
    void queryClient.invalidateQueries({ queryKey: TENANTS_KEY })
  }
}

export function useCreatePlatformTenant(): UseMutationResult<
  PlatformTenantDetail,
  Error,
  PlatformTenantCreate
> {
  const { t } = useTranslation('platform')
  const { accessToken } = useAccessToken()
  const store = useStoreTenantDetail()
  return useMutation({
    mutationFn: (body) => {
      if (!accessToken) throw new Error('not_ready')
      return createPlatformTenant(accessToken, body)
    },
    onSuccess: (detail) => {
      store(detail)
      pushToast({
        kind: 'success',
        message: t('clients.create.success', { name: detail.display_name }),
      })
    },
  })
}

export function usePlatformTenantAction(
  tenantId: string,
): UseMutationResult<PlatformTenantDetail, Error, TenantLifecycleAction> {
  const { t } = useTranslation('platform')
  const { accessToken } = useAccessToken()
  const queryClient = useQueryClient()
  const store = useStoreTenantDetail()
  return useMutation({
    mutationFn: (action) => {
      if (!accessToken) throw new Error('not_ready')
      return runPlatformTenantAction(accessToken, tenantId, action)
    },
    onSuccess: (detail, action) => {
      store(detail)
      if (action.kind === 'grant_credit') {
        // El saldo también se ve en /platform/usage: sin esto tardaría `STALE`.
        for (const key of [GLOBAL_KEY, MARGIN_KEY, TENANT_SUMMARY_KEY]) {
          void queryClient.invalidateQueries({ queryKey: [key] })
        }
      }
      pushToast({ kind: 'success', message: t(`clients.action.${action.kind}.success`) })
    },
  })
}

export function useResendPlatformTenantAdminInvite(
  tenantId: string,
): UseMutationResult<PlatformTenantDetail, Error, void> {
  const { t } = useTranslation('platform')
  const { accessToken } = useAccessToken()
  const store = useStoreTenantDetail()
  return useMutation({
    mutationFn: () => {
      if (!accessToken) throw new Error('not_ready')
      return resendPlatformTenantAdminInvite(accessToken, tenantId)
    },
    onSuccess: (detail) => {
      store(detail)
      pushToast({
        kind: 'success',
        message: t('clients.detail.resend_invite_success', { email: detail.admin_email ?? '' }),
      })
    },
  })
}
